import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthOptional } from '../lib/auth';
import { ErrorBox, useApi } from './kit';
import { useWall, WallBlock } from './credit-wall';
import { DayBars } from './board';
import { FirstBatch } from './first-batch';
import { PeopleCard } from './people';
import { CampaignAngles, Understanding } from './angles';
import { SecretReveal } from '../pages/api-keys';
import { ALL as ALL_SCOPES, PRESETS } from '../lib/scopes';

// What the chat can show besides text (sent by the assistant's tools, never free-form HTML): a table, a Confirm card
// for work that costs credits, a secure form (the Smartlead key, a CSV), the launch card, a chart. Every button here
// is the person's own action through the API; when a card is used it shows as done for the whole team.

export type Widget =
  | { type: 'table'; live?: 'credits'; title: string; columns: Array<{ key: string; label: string }>; rows: Array<Record<string, string | number | null>>; row_link?: string; link?: { path: string; label: string }; done?: Done }
  | { type: 'confirm'; title: string; detail: string; credits?: number; credits_per?: 'day'; usd?: number; usd_per?: 'month'; calls: Array<{ method: 'POST' | 'PUT'; path: string; body?: unknown }>; done_text: string; done?: Done }
  | { type: 'link'; title: string; detail: string; path: string; label: string; done?: Done }
  | { type: 'form'; kind: 'smartlead_key' | 'csv_import' | 'api_key' | 'webhook' | 'evidence_file'; title: string; preset?: Preset; done?: Done }
  | { type: 'launch'; campaign_id: string; title: string; done?: Done }
  | { type: 'first_batch'; campaign_id: string; title: string; done?: Done }
  | { type: 'understanding'; title: string; done?: Done }
  | { type: 'angles'; campaign_id: string; title: string; done?: Done }
  | { type: 'sending_setup'; title: string; done?: Done }
  | { type: 'chart'; title: string; series: Array<{ day: string; sent: number; replied: number }>; done?: Done }
  | { type: 'people'; campaign_id: string; company_ids: string[]; title: string; done?: Done }
  | { type: 'companies'; title: string; columns: Array<{ key: string; label: string }>; rows: Array<Record<string, string | number | null>>; records: Array<Record<string, unknown>>; done?: Done };
type Done = { at: string; by: string; text: string };
type Preset = { name?: string; mode?: 'test' | 'live'; scopes?: string[]; acts_as_me?: boolean; url?: string; events?: string[] };

export function ChatWidget({ w, messageId, index, onChange }: { w: Widget; messageId?: string; index: number; onChange: () => void }) {
  const uid = useId(); // every field has an id: its wrapping label or aria-label is its only name
  const qc = useQueryClient();
  const [done, setDone] = useState<Done | null>(w.done ?? null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const finish = async (text: string) => {
    setDone({ at: new Date().toISOString(), by: 'you', text });
    // the thread keeps it as done; if that fails the card would come back un-done and invite a second Confirm
    if (messageId && /^\d+$/.test(messageId)) {
      await api(`/portal/assistant/messages/${messageId}/widgets/${index}/done`, { method: 'POST', body: { text } })
        .catch((e) => setErr(new Error(`It ran, but the chat could not record it (${(e as Error).message}). Don't press Confirm again.`)));
    }
    void qc.invalidateQueries(); onChange();
  };
  // the calls of a Confirm card that already went through: pressing Confirm again after a failure runs only the rest
  const doneKey = `olv_wg_${messageId ?? 'live'}_${index}`;
  const ran = useRef<Set<number>>(new Set((() => { try { return JSON.parse(sessionStorage.getItem(doneKey) ?? '[]') as number[]; } catch { return []; } })()));
  const markRan = (i: number) => { ran.current.add(i); try { sessionStorage.setItem(doneKey, JSON.stringify([...ran.current])); } catch { /* private mode */ } };
  const run = async (work: () => Promise<string>) => {
    setErr(null); setBusy(true);
    try { await finish(await work()); } catch (e) { setErr(e); } finally { setBusy(false); }
  };
  // who pressed it, when someone else did: a name, never the raw login address (your own showed after a reload)
  const mine = useAuthOptional()?.session?.user.email;
  const by = done?.by && done.by !== 'you' && done.by !== 'Outlevo' && done.by !== mine ? (done.by.includes('@') ? done.by.split('@')[0]!.replace(/\+.*$/, '') : done.by) : '';
  const doneLine = done ? <p className="wg-done" role="status">✓ {done.text}{by ? ` · ${by}` : ''}</p> : null;

  // a quote above the balance (run 13: Confirm was on, and the run spent what there was and dropped the rest): the card
  // says how far short it is, switches Confirm off and offers the way to get more; it asks again while it waits
  const quoted = w.type === 'confirm' && w.credits && !w.credits_per ? w.credits : null;
  const wall = useWall(quoted, { enabled: quoted !== null && !done, refetchMs: 6000 });
  const short = quoted !== null && !done ? (wall.data?.short ?? 0) : 0;

  if (w.type === 'table' && w.live === 'credits') return <LiveCredits w={w} />;
  if (w.type === 'table') {
    const href = (r: Record<string, string | number | null>) => w.row_link ? w.row_link.replace(/\{(\w+)\}/g, (_, k: string) => String(r[k] ?? '')) : null;
    return (
      <div className="wg wg-table">
        <div className="wg-title">{w.title}</div>
        <div className="table-wrap"><table className="table">
          <thead><tr>{w.columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr></thead>
          <tbody>{w.rows.map((r, i) => {
            const to = href(r);
            return <tr key={i}>{w.columns.map((c, j) => <td key={c.key} data-label={c.label}>{j === 0 && to ? <Link to={to}>{String(r[c.key] ?? '')}</Link> : String(r[c.key] ?? '—')}</td>)}</tr>;
          })}</tbody>
        </table></div>
        {w.link ? <Link className="link small" to={w.link.path}>{w.link.label}</Link> : null}
      </div>
    );
  }

  // a step finished on its own page (a card payment in Billing): the button opens it, the page does the rest
  if (w.type === 'link') return <div className="wg" role="group" aria-label={w.title}><div className="wg-title">{w.title}</div><p className="small">{w.detail}</p><div className="wg-actions"><Link className="btn btn-primary" to={w.path}>{w.label}</Link></div></div>;
  if (w.type === 'chart') return <div className="wg"><div className="wg-title">{w.title}</div><DayBars days={w.series} /><p className="muted small">Bars: people reached per day; green: replies.</p></div>;

  if (w.type === 'confirm') {
    return (
      <div className="wg wg-confirm" role="group" aria-label={w.title}>
        <div className="wg-title">{w.title}</div>
        <p className="small">{w.detail}</p>
        {done ? doneLine : (
          <div className="wg-actions">
            {/* while the answer is still streaming the card is not saved yet: Confirm waits for it */}
            <button type="button" className={short > 0 ? 'btn' : 'btn btn-primary'} disabled={busy || !messageId || short > 0} title={short > 0 ? `${short.toLocaleString('en-US')} credits short` : messageId ? undefined : 'One moment: the answer is still being written'} onClick={() => void run(async () => {
              for (const [i, c] of w.calls.entries()) {
                if (ran.current.has(i)) continue;
                await api(c.path, { method: c.method, body: c.body });
                markRan(i);
              }
              return w.done_text;
            })}>{busy ? 'Starting…' : `Confirm${w.credits ? ` · ${w.credits.toLocaleString('en-US')} credits${w.credits_per === 'day' ? ' a day at most' : ''}` : ''}${w.usd ? ` · $${w.usd.toLocaleString('en-US')}${w.usd_per === 'month' ? ' a month' : ''}` : ''}`}</button>
            <button type="button" className="btn" disabled={busy} onClick={() => void finish(`Cancelled: ${w.title}`)}>Cancel</button>
          </div>
        )}
        {short > 0 && wall.data ? <WallBlock wall={wall.data} what={w.title} /> : null}
        {err ? <ErrorBox error={err} /> : null}
      </div>
    );
  }

  if (w.type === 'form' && w.kind === 'smartlead_key') {
    return (
      <div className="wg">
        <div className="wg-title">{w.title}</div>
        {done ? doneLine : (
          // the key goes straight to the API (stored encrypted), never into the conversation or to the model
          <form className="bd-add" onSubmit={(e: FormEvent<HTMLFormElement>) => {
            e.preventDefault(); const key = String(new FormData(e.currentTarget).get('api_key'));
            void run(async () => { await api('/portal/outbound/connect', { method: 'POST', body: { api_key: key } }); return 'Connected Smartlead'; });
          }}>
            <input id={`${uid}-f1`} name="api_key" type="password" required minLength={10} autoComplete="off" placeholder="Smartlead API key" aria-label="Smartlead API key" />
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Connecting…' : 'Connect'}</button>
            <span className="muted small">Stored encrypted. The assistant never sees it.</span>
          </form>
        )}
        {err ? <ErrorBox error={err} /> : null}
      </div>
    );
  }

  if (w.type === 'form' && w.kind === 'api_key') return <div className="wg"><div className="wg-title">{w.title}</div>{done ? doneLine : <ApiKeyForm preset={w.preset} onStored={(t) => void finish(t)} />}</div>;
  if (w.type === 'form' && w.kind === 'webhook') return <div className="wg"><div className="wg-title">{w.title}</div>{done ? doneLine : <WebhookForm preset={w.preset} onStored={(t) => void finish(t)} />}</div>;
  if (w.type === 'form' && w.kind === 'csv_import') return <CsvImport title={w.title} done={done} doneLine={doneLine} busy={busy} run={run} err={err} />;
  if (w.type === 'form' && w.kind === 'evidence_file') return <EvidenceUpload title={w.title} done={done} doneLine={doneLine} busy={busy} run={run} err={err} />;
  // first-batch review: the person's own Approve, Edit and Remove; the card reads the batch live, never a stored copy
  // companies found on the web, each ticked: the person keeps the ones they want; adding is free
  if (w.type === 'companies') return <PickCompanies w={w} done={done} busy={busy} err={err} run={run} storeKey={`olv_pick_${messageId ?? 'live'}_${index}`} />;
  // F35: the people at the kept companies with a channel each; Add is the person's own call
  if (w.type === 'people') return <PeopleCard campaignId={w.campaign_id} companyIds={w.company_ids} title={w.title} done={done} onDone={finish} />;
  if (w.type === 'first_batch') return <div className="wg wg-first-batch" role="group" aria-label={w.title}><div className="wg-title">{w.title}</div><FirstBatch campaignId={w.campaign_id} compact /></div>;
  // before a campaign's emails: the product summary and the angles, read live like the first-batch card
  // confirming or correcting it is the answer to its message's question: recorded like a used card, so that answer's
  // "That's right" buttons go (they stayed live after the card said "You confirmed this", 2026-11-02)
  if (w.type === 'understanding') return <div className="wg wg-understanding" role="group" aria-label={w.title}><div className="wg-title">{w.title}</div><Understanding onDone={(text) => { if (!done) void finish(text); }} /></div>;
  if (w.type === 'angles') return <div className="wg wg-angles" role="group" aria-label={w.title}><div className="wg-title">{w.title}</div><CampaignAngles campaignId={w.campaign_id} compact /></div>;
  if (w.type === 'launch') return <LaunchCard w={w} done={done} doneLine={doneLine} busy={busy} run={run} err={err} />;
  if (w.type === 'sending_setup') return <SendingSetupCard title={w.title} done={done} doneLine={doneLine} busy={busy} run={run} err={err} />;
  return null;
}

type Runner = (work: () => Promise<string>) => Promise<void>;

function CsvImport({ title, done, doneLine, busy, run, err }: { title: string; done: Done | null; doneLine: React.ReactNode; busy: boolean; run: Runner; err: unknown }) {
  const uid = useId(); // every field has an id, so a wrapping label is its only name
  const [file, setFile] = useState<{ id: string; name: string; mapping: Record<string, string>; headers: string[] } | null>(null);
  const [upErr, setUpErr] = useState<unknown>(null);
  const [uploading, setUploading] = useState(false);
  const upload = async (f: File) => {
    setUpErr(null); setUploading(true);
    try {
      const c = await api<{ import_id: string; upload_url: string }>('/v1/imports', { method: 'POST', body: { filename: f.name } });
      const put = await fetch(c.upload_url, { method: 'PUT', body: f, headers: { 'content-type': 'text/csv' } });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      const ins = await api<{ headers: string[]; suggested_mapping: Record<string, string> }>(`/v1/imports/${c.import_id}/uploaded`, { method: 'POST' });
      setFile({ id: c.import_id, name: f.name, mapping: ins.suggested_mapping, headers: ins.headers });
    } catch (e) { setUpErr(e); } finally { setUploading(false); }
  };
  return (
    <div className="wg">
      <div className="wg-title">{title}</div>
      {done ? doneLine : !file ? (
        <label className="btn btn-primary">{uploading ? 'Reading…' : 'Choose a CSV'}<input id={`${uid}-file`} type="file" accept=".csv,text/csv" className="sr-only" disabled={uploading} onChange={(e) => e.target.files?.[0] && void upload(e.target.files[0])} /></label>
      ) : (
        <>
          <p className="small">{file.name}: matched {Object.entries(file.mapping).filter(([, v]) => v).map(([k, v]) => `${k.replace(/_/g, ' ')} ← “${v}”`).join(', ') || 'no columns'}.</p>
          <div className="wg-actions">
            <button type="button" className="btn btn-primary" disabled={busy || !file.mapping.domain} onClick={() => void run(async () => {
              await api(`/v1/imports/${file.id}/mapping`, { method: 'PUT', body: { mapping: Object.fromEntries(Object.entries(file.mapping).filter(([, v]) => v)) } });
              return `Importing ${file.name}`;
            })}>Import</button>
            <Link className="link small" to="/import">Adjust the columns</Link>
          </div>
          {!file.mapping.domain ? <p className="muted small">No website column was found; adjust the columns on the import page.</p> : null}
        </>
      )}
      {upErr ? <ErrorBox error={upErr} /> : null}{err ? <ErrorBox error={err} /> : null}
    </div>
  );
}

// F03: what Outlevo reads (as on the API); the browser's own type for .md is often empty, so the extension decides
const EVIDENCE_MIME: Record<string, string> = {
  pdf: 'application/pdf', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation', txt: 'text/plain', md: 'text/markdown', csv: 'text/csv',
};
const MAX_EVIDENCE_BYTES = 50 * 1024 * 1024;
type EvidenceRead = { id: string; title: string; status: string; error: string | null; statements: Array<{ topic: string; status: string }> };

// A document for Business context, uploaded in the chat: the file goes from the browser to storage (a signed upload URL),
// never through the conversation. Then the card follows the reading and says what it gave: facts added with their quote,
// and proposals for a person to accept or reject.
export function EvidenceUpload({ title, done, doneLine, busy, run, err }: { title: string; done: Done | null; doneLine: React.ReactNode; busy: boolean; run: Runner; err: unknown }) {
  const uid = useId();
  const [id, setId] = useState<string | null>(null);
  const [pickErr, setPickErr] = useState<string | null>(null);
  const [stop, setStop] = useState(false);
  const ev = useApi<EvidenceRead>(id ? `/v1/evidence/${id}` : null, { refetchMs: stop ? undefined : 3_000 });
  const finished = Boolean(ev.data && (ev.data.status === 'extracted' || ev.data.status === 'failed'));
  useEffect(() => { if (finished) setStop(true); }, [finished]);
  const upload = (f: File) => {
    setPickErr(null);
    const mime = EVIDENCE_MIME[f.name.split('.').pop()?.toLowerCase() ?? ''];
    if (!mime) { setPickErr('Upload a PDF, DOCX, PPTX, TXT, MD or CSV file.'); return; }
    if (f.size > MAX_EVIDENCE_BYTES) { setPickErr(`Files are limited to 50 MB (this one is ${(f.size / 1048576).toFixed(1)} MB).`); return; }
    void run(async () => {
      const c = await api<{ id: string; upload?: { method: 'PUT'; url: string; headers: Record<string, string> } }>('/v1/evidence', { method: 'POST', body: {
        kind: 'file', title: f.name.replace(/\.[^.]+$/, '').slice(0, 300) || f.name, filename: f.name.slice(0, 255), mime_type: mime, size_bytes: f.size } });
      if (!c.upload) throw new Error('No upload address came back; try again');
      const put = await fetch(c.upload.url, { method: 'PUT', body: f, headers: c.upload.headers });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      await api(`/v1/evidence/${c.id}/complete`, { method: 'POST' });
      setId(c.id);
      return `Uploaded ${f.name}; Outlevo is reading it`;
    });
  };
  const facts = (ev.data?.statements ?? []).filter((x) => x.topic !== 'unknown');
  const proposed = facts.filter((x) => x.status === 'proposed').length;
  return (
    <div className="wg">
      <div className="wg-title">{title}</div>
      {done ? doneLine : (
        <>
          <label className="btn btn-primary">{busy ? 'Uploading…' : 'Choose a document'}<input id={`${uid}-file`} type="file" accept=".pdf,.docx,.pptx,.txt,.md,.csv" className="sr-only" disabled={busy} onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} /></label>
          <p className="muted small">PDF, DOCX, PPTX, TXT, MD or CSV, up to 50 MB: case studies, product docs, decks. It goes straight to Outlevo; the assistant never sees the file.</p>
        </>
      )}
      {id ? (
        <p className="small ev-read" role="status">
          {!ev.data || !finished ? <><span className="spinner" aria-hidden="true" /> Reading it… this takes a minute or two.</>
            : ev.data.status === 'failed' ? <>Not read: {ev.data.error ?? 'no readable text'}.</>
            : <>Read: {facts.length - proposed} fact{facts.length - proposed === 1 ? '' : 's'} added with their quote, {proposed} proposal{proposed === 1 ? '' : 's'} to review. <Link className="link" to={`/context?tab=evidence&id=${id}`}>See the facts</Link>{proposed ? <> · <Link className="link" to="/context?tab=proposals">Review the proposals</Link></> : null}</>}
        </p>
      ) : done ? <p className="small"><Link className="link" to="/context?tab=evidence">Open the Evidence library</Link></p> : null}
      {pickErr ? <p className="error small" role="alert">{pickErr}</p> : null}
      {err ? <ErrorBox error={err} /> : null}
    </div>
  );
}

type Campaign = { id: string; name: string; status: string; problems: { approval: string[]; senders: string[] } };
type Stats = { leads: number; pushed: number; sent: number };
function LaunchCard({ w, done, doneLine, busy, run, err }: { w: Extract<Widget, { type: 'launch' }>; done: Done | null; doneLine: React.ReactNode; busy: boolean; run: Runner; err: unknown }) {
  const c = useApi<Campaign>(`/v1/campaigns/${w.campaign_id}`, { refetchMs: done ? undefined : 10_000 });
  const s = useApi<Stats>(`/v1/campaigns/${w.campaign_id}/stats`);
  const problems = c.data ? [...c.data.problems.approval, ...c.data.problems.senders] : [];
  const startable = c.data && ['built', 'paused'].includes(c.data.status) && !problems.length;
  return (
    <div className="wg wg-launch" role="group" aria-label={`Launch ${c.data?.name ?? 'campaign'}`}>
      <div className="wg-title">{c.data ? `${w.title} · ${c.data.name}` : w.title}</div>
      {c.data ? (
        <>
          <ul className="wg-checks">
            <li className={problems.length ? '' : 'ok'}>{problems.length ? `${problems.length} thing${problems.length === 1 ? '' : 's'} before it can send` : 'Every check passes'}</li>
            {problems.slice(0, 4).map((p) => <li key={p}>{p}</li>)}
            <li className={c.data.status === 'built' || c.data.status === 'paused' || c.data.status === 'active' ? 'ok' : ''}>Built in Smartlead: {['built', 'paused', 'active'].includes(c.data.status) ? 'yes' : c.data.status === 'building' ? 'building…' : 'not yet'}</li>
            {s.data ? <li className={s.data.leads ? 'ok' : ''}>{s.data.leads} lead{s.data.leads === 1 ? '' : 's'}</li> : null}
          </ul>
          {done ? doneLine : c.data.status === 'active' ? <p className="wg-done">✓ Sending</p> : (
            <div className="wg-actions">
              <button type="button" className="btn btn-primary" disabled={!startable || busy} onClick={() => {
                if (!confirm(`Start sending "${c.data!.name}"? Every rule is checked again first.`)) return;
                void run(async () => { await api(`/portal/campaigns/${w.campaign_id}/start`, { method: 'POST' }); return `Started ${c.data!.name}`; });
              }}>Start sending</button>
              <Link className="link small" to={`/campaigns/${w.campaign_id}`}>Open the campaign</Link>
            </div>
          )}
        </>
      ) : c.error ? <ErrorBox error={c.error} /> : <p className="muted small">Loading…</p>}
      {err ? <ErrorBox error={err} /> : null}
    </div>
  );
}

// A new API key, made in the chat: the assistant's suggestion is prefilled; the secret is shown once here and goes
// nowhere else (not into the conversation, not to the assistant). The card is done once the person has stored it.
function ApiKeyForm({ preset, onStored }: { preset?: Preset; onStored: (doneText: string) => void }) {
  const uid = useId();
  const [mode, setMode] = useState<'test' | 'live'>(preset?.mode ?? 'test');
  const [scopes, setScopes] = useState<Set<string>>(new Set((preset?.scopes ?? PRESETS.integration!.scopes).filter((x) => ALL_SCOPES.includes(x))));
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [made, setMade] = useState<{ secret: string; name: string; prefix: string; mode: string } | null>(null);
  if (made) return <SecretReveal title={`Your ${made.mode} key “${made.name}”`} secret={made.secret} onDone={() => onStored(`Created the ${made.mode} key “${made.name}” (${made.prefix}…); the secret was shown once`)} />;
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault(); setErr(null); setBusy(true);
    const f = new FormData(e.currentTarget);
    try {
      const r = await api<{ secret: string; name: string; prefix: string; mode: string }>('/portal/api-keys', { method: 'POST', body: {
        name: String(f.get('name')), mode, scopes: ALL_SCOPES.filter((x) => scopes.has(x)), user_bound: f.get('user_bound') === 'on',
        ...(mode === 'live' ? { caps: { day_cap: Number(f.get('day_cap')), month_cap: Number(f.get('month_cap')), per_call_max: Number(f.get('per_call_max')) } } : {}),
      } });
      setMade(r);
    } catch (e2) { setErr(e2); } finally { setBusy(false); }
  };
  const toggle = (x: string, on: boolean) => setScopes((cur) => { const n = new Set(cur); if (on) n.add(x); else n.delete(x); return n; });
  const shown = more ? ALL_SCOPES : ALL_SCOPES.filter((x) => scopes.has(x));
  return (
    <form className="wg-form" onSubmit={(e) => void submit(e)}>
      <label>Name <input id={`${uid}-name`} name="name" required maxLength={80} defaultValue={preset?.name ?? 'My app'} /></label>
      <div className="seg-text" role="radiogroup" aria-label="Mode">
        {(['test', 'live'] as const).map((m) => <button key={m} type="button" role="radio" aria-checked={mode === m} onClick={() => setMode(m)}>{m === 'test' ? 'Test (sandbox)' : 'Live'}</button>)}
      </div>
      <fieldset className="wg-scopes"><legend>Scopes <span className="muted small">({scopes.size})</span></legend>
        {shown.map((x) => <label key={x} className="check"><input id={`${uid}-${x}`} type="checkbox" checked={scopes.has(x)} onChange={(e) => toggle(x, e.target.checked)} /> <code>{x}</code></label>)}
        <button type="button" className="link small" onClick={() => setMore((v) => !v)}>{more ? 'Show only the chosen ones' : 'Add more scopes'}</button>
      </fieldset>
      <label className="check"><input id={`${uid}-user-bound`} type="checkbox" name="user_bound" defaultChecked={preset?.acts_as_me} /><span>Acts as me <span className="muted small">· needed for campaign writes and handoff decisions</span></span></label>
      {mode === 'live' ? (
        <fieldset className="wg-caps"><legend>Spend caps (credits)</legend>
          <label>Per day <input id={`${uid}-day`} name="day_cap" type="number" min={0} required defaultValue={100} /></label>
          <label>Per month <input id={`${uid}-month`} name="month_cap" type="number" min={0} required defaultValue={1000} /></label>
          <label>Per call <input id={`${uid}-call`} name="per_call_max" type="number" min={0} required defaultValue={30} /></label>
          <span className="muted small">A live key needs a second factor from the last 12 hours.</span>
        </fieldset>
      ) : <span className="muted small">Works in your sandbox: fake data, no credits.</span>}
      <button type="submit" className="btn btn-primary" disabled={busy || !scopes.size}>{busy ? 'Creating…' : `Create the ${mode} key`}</button>
      {err ? <ErrorBox error={err} /> : null}
    </form>
  );
}

const WEBHOOK_EVENTS = ['lead.replied', 'lead.classified', 'lead.answered', 'meeting.booked', 'lead.sent', 'lead.bounced', 'lead.unsubscribed', 'company.qualified', 'company.disqualified', 'company.created', 'contact.verified',
  'handoff.created', 'handoff.accepted', 'handoff.rejected', 'campaign.status_changed', 'revenue_action.proposed', 'revenue_action.approved', 'revenue_action.executed', 'revenue_action.failed', 'revenue_action.blocked', 'revenue_action.outcome_observed', 'contact.discovered', 'evidence.added', 'job.completed', 'job.failed', 'outcome.recorded', 'usage.threshold'];

// A webhook, made in the chat: URL and events, then the signing secret once, and a signed test delivery.
function WebhookForm({ preset, onStored }: { preset?: Preset; onStored: (doneText: string) => void }) {
  const uid = useId();
  const [events, setEvents] = useState<Set<string>>(new Set((preset?.events ?? ['lead.replied', 'company.qualified']).filter((x) => WEBHOOK_EVENTS.includes(x))));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [made, setMade] = useState<{ id: string; url: string; secret: string } | null>(null);
  const [test, setTest] = useState<string | null>(null);
  if (made) {
    const send = async () => {
      setTest('Sending…');
      try { const r = await api<{ delivered: boolean; status_code: number | null; error?: string }>(`/v1/webhooks/${made.id}/test`, { method: 'POST' }); setTest(r.delivered ? `Delivered: your endpoint answered ${r.status_code}` : `Not delivered: ${r.error ?? `your endpoint answered ${r.status_code}`}`); }
      catch (e) { setTest((e as Error).message); }
    };
    return <>
      <SecretReveal title="The webhook's signing secret" secret={made.secret} onDone={() => onStored(`Created a webhook to ${made.url} (${[...events].join(', ')}); the secret was shown once`)} />
      <p className="small"><button type="button" className="btn btn-sm" onClick={() => void send()}>Send a test event</button> {test ? <span role="status">{test}</span> : null}</p>
    </>;
  }
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault(); setErr(null); setBusy(true);
    const url = String(new FormData(e.currentTarget).get('url'));
    try { const r = await api<{ id: string; secret: string }>('/v1/webhooks', { method: 'POST', body: { url, events: [...events] } }); setMade({ id: r.id, url, secret: r.secret }); }
    catch (e2) { setErr(e2); } finally { setBusy(false); }
  };
  return (
    <form className="wg-form" onSubmit={(e) => void submit(e)}>
      <label>Your endpoint (HTTPS) <input id={`${uid}-url`} name="url" type="url" required placeholder="https://your.app/api/outlevo-webhook" defaultValue={preset?.url} /></label>
      <fieldset className="wg-scopes"><legend>Events</legend>
        {WEBHOOK_EVENTS.map((x) => <label key={x} className="check"><input id={`${uid}-${x}`} type="checkbox" checked={events.has(x)} onChange={(e) => setEvents((cur) => { const n = new Set(cur); if (e.target.checked) n.add(x); else n.delete(x); return n; })} /> <code>{x}</code></label>)}
      </fieldset>
      <button type="submit" className="btn btn-primary" disabled={busy || !events.size}>{busy ? 'Creating…' : 'Create the webhook'}</button>
      {err ? <ErrorBox error={err} /> : null}
    </form>
  );
}

type Sending = {
  mode: 'managed' | 'own_key' | 'none';
  allowance: { plan: string; domains: number; mailboxes: number; used_domains: number; used_mailboxes: number; sandbox: boolean; contract_pending: boolean };
  proposing: boolean; propose_error: string | null; propose_none?: { checked: number } | null; proposals: Array<{ domain: string; price_usd: number | null }>;
  domains: Array<{ id: string; domain: string; status: string; step: string; last_error: string | null; ready_expected_at: string | null; mailboxes: Array<{ email: string; name: string; status: string }> }>;
  stage: string; ready_around: string | null; summary: string; next: string;
};
const STAGE_TEXT: Record<string, string> = { purchasing: 'Buying', dns_pending: 'DNS', mailboxes_creating: 'Mailboxes', warming: 'Warming up', ready: 'Ready', failed: 'Stopped' };

// F27: managed sending in the chat. Outlevo proposes lookalike domains; the person picks within the plan, names the
// senders, and an owner or admin confirms. Afterwards the card follows each domain to ready (or says what stopped).
function SendingSetupCard({ title, done, doneLine, busy, run, err }: { title: string; done: Done | null; doneLine: React.ReactNode; busy: boolean; run: Runner; err: unknown }) {
  const uid = useId();
  const s = useApi<Sending>('/v1/sending', { refetchMs: 8_000 });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [senders, setSenders] = useState<Array<{ first_name: string; last_name: string }>>([{ first_name: '', last_name: '' }]);
  if (!s.data) return <div className="wg"><div className="wg-title">{title}</div>{s.error ? <ErrorBox error={s.error} /> : <p className="muted small">Loading…</p>}</div>;
  const d = s.data;
  const a = d.allowance;
  const freeDomains = Math.max(0, a.domains - a.used_domains);
  const perDomain = Math.max(1, Math.min(5, Math.floor((a.mailboxes - a.used_mailboxes) / Math.max(1, picked.size || 1))));
  const named = senders.filter((x) => x.first_name.trim() && x.last_name.trim());
  const fits = picked.size >= 1 && picked.size <= freeDomains && named.length >= 1 && picked.size * named.length <= a.mailboxes - a.used_mailboxes;
  const setSender = (i: number, k: 'first_name' | 'last_name', v: string) => setSenders((cur) => cur.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const started = d.domains.length > 0;
  return (
    <div className="wg" role="group" aria-label={title}>
      <div className="wg-title">{title}</div>
      <p className="small">{d.summary}</p>
      {started ? (
        <ul className="wg-checks">
          {d.domains.map((x) => (
            <li key={x.id} className={x.status === 'ready' ? 'ok' : ''}>
              <strong>{x.domain}</strong> · {STAGE_TEXT[x.status] ?? x.step}{x.status === 'warming' && x.ready_expected_at ? ` until ~${new Date(x.ready_expected_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
              {x.last_error ? <span className="muted small"> — {x.last_error}</span> : null}
              {x.status === 'failed' ? <> <button type="button" className="link small" disabled={busy} onClick={() => void run(async () => { await api(`/portal/sending/domains/${x.id}/retry`, { method: 'POST' }); return `Retrying ${x.domain}`; })}>Retry</button></> : null}
              <span className="muted small"> · {x.mailboxes.map((m) => m.email).join(', ')}</span>
            </li>
          ))}
        </ul>
      ) : done ? doneLine : d.mode === 'own_key' || !a.domains ? (
        <p className="muted small">{d.next} <Link className="link" to="/billing">Billing</Link> · <Link className="link" to="/integrations">Connect your own Smartlead</Link></p>
      ) : d.proposing || (!d.proposals.length && !d.propose_error && !d.propose_none) ? (
        <p className="muted small">Checking which names are free…</p>
      ) : d.propose_error ? <p className="small">Could not check the names: {d.propose_error}</p>
      : !d.proposals.length && d.propose_none ? (
        <p className="small">None of the {d.propose_none.checked} lookalike names checked is free to buy. Ask the Outlevo team in the chat for another name, or <Link className="link" to="/integrations">connect your own Smartlead</Link>.</p>
      ) : (
        <form className="wg-form" onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            const r = await api<{ domains: string[]; mailboxes: number }>('/portal/sending/setup', { method: 'POST', body: { domains: [...picked], senders: named } });
            return `Setting up ${r.domains.join(', ')} with ${r.mailboxes} mailbox${r.mailboxes === 1 ? '' : 'es'}`;
          });
        }}>
          <fieldset className="wg-scopes"><legend>Domains to send from · pick up to {freeDomains} ({a.plan})</legend>
            {d.proposals.map((p) => (
              <label key={p.domain} className="check"><input id={`${uid}-${p.domain}`} type="checkbox" checked={picked.has(p.domain)} disabled={!picked.has(p.domain) && picked.size >= freeDomains}
                onChange={(e) => setPicked((cur) => { const n = new Set(cur); if (e.target.checked) n.add(p.domain); else n.delete(p.domain); return n; })} /> <code>{p.domain}</code></label>
            ))}
          </fieldset>
          <fieldset className="wg-scopes"><legend>Senders (real people at your company; one mailbox each per domain)</legend>
            {senders.map((x, i) => (
              <span key={i} className="wg-actions">
                <input id={`${uid}-first-${i}`} aria-label={`Sender ${i + 1} first name`} placeholder="First name" maxLength={60} value={x.first_name} onChange={(e) => setSender(i, 'first_name', e.target.value)} />
                <input id={`${uid}-last-${i}`} aria-label={`Sender ${i + 1} last name`} placeholder="Last name" maxLength={60} value={x.last_name} onChange={(e) => setSender(i, 'last_name', e.target.value)} />
              </span>
            ))}
            {senders.length < perDomain ? <button type="button" className="link small" onClick={() => setSenders((cur) => [...cur, { first_name: '', last_name: '' }])}>Add a sender</button> : null}
          </fieldset>
          <p className="muted small">Included in your plan: {a.domains} domain{a.domains === 1 ? '' : 's'}, {a.mailboxes} mailboxes. Setup takes about a day, then 2–3 weeks of warm-up before campaigns send. Owner or admin.</p>
          <button type="submit" className="btn btn-primary" disabled={busy || !fits}>{busy ? 'Setting up…' : `Confirm · ${picked.size * named.length || 0} mailbox${picked.size * named.length === 1 ? '' : 'es'}`}</button>
        </form>
      )}
      {err ? <ErrorBox error={err} /> : null}
    </div>
  );
}

/** The companies found on the web as a table with a tick on each row (all ticked); Add posts only the ticked ones. */
function PickCompanies({ w, done, busy, err, run, storeKey }: { w: Extract<Widget, { type: 'companies' }>; done: Done | null; busy: boolean; err: unknown; run: (work: () => Promise<string>) => Promise<void>; storeKey: string }) {
  // the unticked rows survive a reload (live 2026-10-30: all 15 came back ticked, inviting "Add 15" by mistake)
  const [keep, setKeep] = useState<Set<number>>(() => {
    try { const off = JSON.parse(localStorage.getItem(storeKey) ?? '[]') as number[]; return new Set(w.rows.map((_, i) => i).filter((i) => !off.includes(i))); } catch { return new Set(w.rows.map((_, i) => i)); }
  });
  const toggle = (i: number) => setKeep((cur) => {
    const n = new Set(cur); if (n.has(i)) n.delete(i); else n.add(i);
    try { localStorage.setItem(storeKey, JSON.stringify(w.rows.map((_, j) => j).filter((j) => !n.has(j)))); } catch { /* private mode */ }
    return n;
  });
  const n = keep.size;
  return (
    <div className="wg wg-table wg-pick" role="group" aria-label={w.title}>
      <div className="wg-title">{w.title}</div>
      <div className="table-wrap"><table className="table">
        <thead><tr><th><span className="sr-only">Keep</span></th>{w.columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr></thead>
        <tbody>{w.rows.map((r, i) => (
          <tr key={i} className={keep.has(i) ? '' : 'muted'}>
            <td><input type="checkbox" checked={keep.has(i)} disabled={Boolean(done) || busy} onChange={() => toggle(i)} aria-label={`Keep ${String(r.name ?? r.domain)}`} /></td>
            {w.columns.map((c) => <td key={c.key} data-label={c.label}>{String(r[c.key] ?? '—')}</td>)}
          </tr>
        ))}</tbody>
      </table></div>
      {done ? <p className="wg-done" role="status">✓ {done.text}</p> : (
        <div className="wg-actions">
          <button type="button" className="btn btn-primary" disabled={busy || !n} onClick={() => void run(async () => {
            const records = w.records.filter((_, i) => keep.has(i));
            await api('/v1/companies/bulk', { method: 'POST', body: { records } });
            return `Added ${records.length} ${records.length === 1 ? 'company' : 'companies'} to Accounts: ${records.map((x) => String(x.domain)).join(', ')}`.slice(0, 280);
          })}>{busy ? 'Adding…' : n ? `Add ${n} ${n === 1 ? 'company' : 'companies'}` : 'Tick the companies to add'}</button>
          <span className="muted small">Free. Untick any you don't want. Checking them against your ICP is quoted next.</span>
        </div>
      )}
      {err ? <ErrorBox error={err} /> : null}
    </div>
  );
}

type UsageNow = { available: number; by_operation: Array<{ operation: string | null; credits: number; count: number }> };

/** The credits card of a chat answer, read again each time it is shown (the saved one said "40 available · 0 spent" after all 40 were spent). */
function LiveCredits({ w }: { w: Extract<Widget, { type: 'table' }> }) {
  const u = useApi<UsageNow>('/v1/usage');
  const ops = u.data?.by_operation ?? null;
  const spent = ops ? ops.reduce((n, o) => n + Number(o.credits), 0) : null;
  const nice = (op: string | null) => (op ?? 'other').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
  const rows: Array<Record<string, string | number | null>> = ops ? ops.map((o) => ({ what: nice(o.operation), times: o.count, credits: o.credits })) : w.rows;
  const title = u.data && spent !== null ? `${Number(u.data.available).toLocaleString('en-US')} credits available · ${spent.toLocaleString('en-US')} spent this cycle` : w.title;
  return (
    <div className="wg wg-table">
      <div className="wg-title">{title}</div>
      <div className="table-wrap"><table className="table">
        <thead><tr>{w.columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i}>{w.columns.map((c) => <td key={c.key} data-label={c.label}>{String(r[c.key] ?? '—')}</td>)}</tr>)}</tbody>
      </table></div>
      {w.link ? <Link className="link small" to={w.link.path}>{w.link.label}</Link> : null}
    </div>
  );
}
